// HeliodorSDK

import {hel_get_QueryParam} from './heliodor_core_gui_callee';
import {checkAndInitVR, checkVR} from './heliodor_xr';
import {presetBeforeBoot} from './logics/assign';
import {hel_audio_SuspendAll, hel_audio_ResumeAll, initMainAudioContext} from './logics/audio';
import {hel_resizeWindow} from './logics/callCGui';
import {
  ETextParamType_SetMicState,
  ETextParamType_Logined,
  ETextParamType_CanvasFileName,
  ETextParamType_CurrentUserName,
  ETextParamType_CompImageListFileName,
  ETextParamType_AddPackFile,
  ETextParamType_GetFirstLoadingRate
} from './logics/const';
import {hel_mixer_LoadCookie_Mute, hel_mixer_LoadCookie_Volume} from './logics/cookieOps';
import {
  addPackFile,
  getCanvasFileName,
  getCompImageListFileName,
  getCompImageListUseCDN,
  getCurrentUserName,
  getLoggedInStatusToggle,
  getPackFileList,
  getVCUID,
  getWorldId,
  setCompImageListFileName,
  setDataFolderName,
  setMixerTab,
  setSceneJsonPath,
  setVCUID,
  setInitInteractedFlg
} from './logics/dataOps';
import {initializeGamePad} from './logics/device';
import {defineSetstateForHeliModule} from './logics/globals';
import {hel_canvas_ResetToggleDefault} from './logics/gui_caller';
import {
  drawFrame,
  hel_CallSceneComponent,
  hel_CallCanvasComponent,
  hel_cancelAnimationFrame,
  hel_requestAnimationFrame,
  hel_setCanvasFileName,
  hel_SetServerTime,
  onLoadedDeferred,
  requestBootStrapGameEngine
} from './logics/lib';
import {getMainCanvasDOMElement} from './logics/mainCanvasDOM';
import {hel_mixer_UpdateAll} from './logics/mixerOps';
import {
  requestOnWindowActivate,
  requestOnBeforeUnload,
  requestOnUnload,
  hel_DispatchEvent,
  requestPreBootStrapGameEngine,
  hel_setText
} from './logics/moduleCaller';
import {requestResizeWindow} from './logics/moduleCaller';
import {
  hel_isMobile,
  hel_isAndroid,
  hel_isAndroidLineIAB,
  hel_isLineMiniApp,
  hel_isLangJa,
  hel_isDirectPageAccess
} from './logics/platform';
import {
  hel_get_cookie,
  hel_gtag,
  hel_set_cookie,
  hel_set_show_TextField_configurl,
  hel_set_show_TextField_UserName,
  setLiveMicEnabled,
  setMicEnabled
} from './logics/shared';
import {setupVideoInitEvents} from './logics/video_callee';
import {initFirstInteractWall} from './logics/widgetFirstIntractDialog';

declare global {
  /** heliodor_post.js で定義されるグローバル変数。 */
  // eslint-disable-next-line no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須
  var isWasmInitialized: true | undefined;
}

const afterBootStrapGameEngine = (mainCanvas: HTMLCanvasElement) => {
  initializeGamePad();

  //
  mainCanvas.focus();

  if (mainCanvas !== undefined) {
    requestResizeWindow(mainCanvas);
  }

  drawFrame();
};

/**
 * JSから参照あり
 * iframeで開いているなら親ページにメッセージをポストする
 * iframeでワールド遷移するとマイクがブロック状態になる問題への対処
 */
const postMessageIfiframe = (spatiumCode: string) => {
  const worldURL = window.location.href;

  // worldid取得
  const worldid = hel_get_QueryParam('worldid', worldURL) || '';

  // roomid取得（存在しない場合は送らない）
  const roomid = hel_get_QueryParam('roomid', worldURL) || '';

  // 送信メッセージ作成
  const message: any = {
    event_type: 'world_move',
    worldid: worldid
  };

  if (roomid) {
    message.roomid = roomid;
  }

  if (spatiumCode) {
    message.spatium_code = spatiumCode;
  }

  // iframeで開いているなら親ページにメッセージをポストする
  if (window !== window.parent) {
    window.parent.postMessage(message, '*');
  }
};

const hel_makeVCUID = () => {
  let Result = '1';
  for (let n = 1; n < 10; n++) {
    Result += String(Math.floor(Math.random() * 10));
  }
  return Result;
};

const hel_prepareVCUID = () => {
  const VCUID_CookieName = 'VCUID';
  setVCUID(hel_get_cookie(VCUID_CookieName) || '');
  const vcuid = getVCUID();
  if (vcuid === undefined) {
    const updatedVcuid = hel_makeVCUID();
    setVCUID(updatedVcuid);
    hel_set_cookie(VCUID_CookieName, updatedVcuid);
  }
};

/** C++側から検索を依頼されたJS関数を登録しておくためのテーブル */
const heliport_funcTable: Function[] = [];

const hel_start = (
  Type: number,
  CharaType: number,
  Param1: number,
  MultiPlay: boolean,
  StrParam0: string
) => {
  defineSetstateForHeliModule();
  const mainCanvas = getMainCanvasDOMElement();
  if (!mainCanvas || mainCanvas.width === 0 || mainCanvas.height === 0) {
    throw new Error('[ hel_start ] mainCanvas is not assigned. noop');
  }

  const CanTouch = window.ontouchstart !== undefined;

  setSceneJsonPath(globalThis.g_DataFolderName + '/' + StrParam0);

  // Initialize
  hel_mixer_LoadCookie_Volume();
  hel_mixer_LoadCookie_Mute();

  /**
   * @remarks
   * hel_startより呼出し有り
   */
  hel_prepareVCUID();

  hel_set_show_TextField_configurl(false);
  hel_set_show_TextField_UserName(false);

  // gtag呼び出し
  hel_gtag('Start', {param0: getWorldId(), param1: MultiPlay});

  // Set Parameter
  presetBeforeBoot();
  hel_setText(ETextParamType_Logined, getLoggedInStatusToggle() ? '1' : '0');

  const packFileList = getPackFileList();
  if (!packFileList) {
    throw new Error('getPackFileList() returns undefined');
  }
  for (const packFile of packFileList) {
    hel_setText(ETextParamType_AddPackFile, packFile);
  }

  hel_setText(ETextParamType_CanvasFileName, getCanvasFileName());
  hel_setText(
    ETextParamType_CompImageListFileName,
    getCompImageListFileName() + ',' + (getCompImageListUseCDN() ? 'true' : 'false')
  );
  hel_setText(ETextParamType_CurrentUserName, getCurrentUserName());

  //

  const params = new URL(document.location as any).searchParams;
  const webgpu = params.get('webgpu');
  if (webgpu === '1') {
    // WebGPUの場合は初期化処理の都合で、requestPreBootStrapGameEngine()を呼び出して暫くしてからrequestBootStrapGameEngine()を呼び出す必要がある。
    requestPreBootStrapGameEngine();
    window.setTimeout(() => {
      requestBootStrapGameEngine(Type, CharaType, Param1, StrParam0, CanTouch, MultiPlay, false);
      afterBootStrapGameEngine(mainCanvas);
    }, 1000);
  } else {
    requestBootStrapGameEngine(Type, CharaType, Param1, StrParam0, CanTouch, MultiPlay, false);
    afterBootStrapGameEngine(mainCanvas);
  }
};

// 即時実行
document.addEventListener(
  'visibilitychange',
  () => {
    // ウィンドウアクティブ・非アクティブイベント
    requestOnWindowActivate(document.visibilityState !== 'hidden');

    // PCではタブの非アクティブでミュートにしない
    if (!hel_isMobile()) return;

    // 以降はミュート関連の処理
    if (document.visibilityState === 'hidden') {
      // iOSでWebブラウザが非アクティブから復帰したときに音楽再生を復元出来ないので、
      // ここですべての音楽をサスペンドさせる。復帰はアクティブ後の初回タップでおこなう
      hel_audio_SuspendAll();

      //
      setMixerTab(0.0);
      hel_mixer_UpdateAll();

      // Force MicMute
      setMicEnabled(false);
      setLiveMicEnabled(false);

      hel_setText(ETextParamType_SetMicState, '0');
      hel_canvas_ResetToggleDefault('voicechat');
    } else if (document.visibilityState === 'visible') {
      setMixerTab(1.0);
      hel_mixer_UpdateAll();
    }
  },
  false
);
document.addEventListener('fullscreenchange', () => hel_resizeWindow());

// iOSでWebブラウザが非アクティブから復帰したときに音楽再生を復元出来ないのでタップ後にレジュームさせる
document.addEventListener('touchend', () => hel_audio_ResumeAll());

//
window.addEventListener('beforeunload', (event: Event) => requestOnBeforeUnload());
window.addEventListener('pagehide', (event: PageTransitionEvent) => requestOnUnload());

const openInExternalBrowser = (): void => {
  const url = location.href;
  const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
  const dialog = document.createElement('dialog');
  const messageTextData = hel_isLangJa()
    ? {
        header: 'ご利用中のLINE内ブラウザでは一部の機能が正しく動作しない可能性があります。',
        body: '以下のリンクを既定のブラウザで開いてください。',
        back: '戻る'
      }
    : {
        header: 'Some features may not work properly in the LINE app internal browser.',
        body: 'Please open the link below in your default browser.</p>',
        back: 'back'
      };
  dialog.innerHTML = `<h1>${messageTextData.header}</h1><p>${messageTextData.body}</p><p>URL: <a href="${location.href}">${location.href}</a></p>`;
  if (!hel_isDirectPageAccess()) {
    dialog.innerHTML += `<p><a href="#" onClick="history.back(); return false;">${messageTextData.back}</a></p>`;
  }
  document.body.append(dialog);
  dialog.showModal();
  location.href = intentUrl;
};

Object.assign(globalThis, {
  /**
   * @remarks
   * JRWorld_web->heliodor_front.jsにて参照
   * JRWorld_web->heliodor_xr.jsにて参照
   * XRWorldIngame->heliodor_front.jsにて参照
   * XRWorldIngame->heliodor_xr.jsにて参照
   * XRWorldIngame->heliodor_xrw_main.jsにて参照
   * front, heliodor_xrw_mainから呼び出しがあるためグローバルにとどめてる
   */
  hel_setCanvasFileName,

  // パックファイルの追加

  HSAddPackPriority_Top: 0,
  HSAddPackPriority_Default: 1,

  HSAddPackContidion_All: 0,
  HSAddPackContidion_UseAstc: 1,
  HSAddPackContidion_UseEtc2: 2,
  HSAddPackContidion_UnuseTexComp: 3,

  hel_addPackFile: (packFileName: string, priority: Number, condition: Number, useCDN: boolean) => {
    addPackFile(packFileName, priority, condition, useCDN);
  },

  /**
   * @remarks
   * 圧縮画像リストを使用するときにファイル名を設定する
   */
  hel_setCompImageListFileName: (compImageListFileName: string, useCDN: boolean) => {
    setCompImageListFileName(compImageListFileName, useCDN);
  },

  //

  hel_SetDataFolderName: (DataFolderName: string) => {
    setDataFolderName(DataFolderName);
  },

  hel_requestAnimationFrame,

  hel_cancelAnimationFrame,

  /**
   * @remarks
   * frontから呼び出しがあるためグローバルにとどめてる
   */
  hel_start,

  /**
   * frontから呼び出しがあるためグローバルにとどめてる
   * @virtual
   */
  hel_startScene: (
    CharaType: number,
    MultiPlay: boolean,
    SceneFileName: string,
    IsVR: boolean = false
  ) => {
    // LINEのIn-App Browser（アプリ内ブラウザ NOTミニアプリ）
    if (hel_isAndroidLineIAB()) {
      openInExternalBrowser();
      return;
    }
    // AudioContextは1タブ1AudioContextを共用すればいいのであらかじめ初期化
    // TODO: この初期化をユーザータップ時に行い、BGMの初期間も同時にすれば、マイク無許可でもリモート音声の自動再生ができる説
    initMainAudioContext();
    if (IsVR) {
      checkAndInitVR(SceneFileName, MultiPlay ? 1 : 0);
    } else {
      hel_start(0, CharaType, 0, MultiPlay, SceneFileName);
    }
  },

  /**
   * Helodorのシーンファイル読み込み完了時に呼ばれるコールバック
   */
  hel_onSceneFileLoaded: (spatiumCode: string) => {
    setupVideoInitEvents();
    postMessageIfiframe(spatiumCode);
  },

  /**
   * Heliodorのメインロード完了時に呼ばれるコールバック関数
   */
  hel_onloadedForSystem: () => {
    onLoadedDeferred.resolve();

    checkVR(); // WebXRリリース延期のためコメントアウト

    // ローディング画面に不具合があってもエンジン側から非表示にする
    const loadingElement = document.getElementById('loading-guid-html');
    if (loadingElement) {
      loadingElement.remove();
    }

    // ※暫定不具合回避実装（Android版ミニアプリ対応）
    // もしAndroid版LINEミニアプリだった場合、「CLICK TO PLAY」レイヤーを被せる
    if (hel_isAndroid() && hel_isLineMiniApp()) {
      // クリックしたらinteractFlgを立てる
      initFirstInteractWall(() => {
        setInitInteractedFlg(true);
      });
    }
  },

  // 以下の関数はダブってるので削除した(sdk3.8: 2023/3/6 nishida）
  //  hel_setCanvasLayerShow

  /**
   * オブジェクト名と関数名で関数を探し、見つかった関数をテーブルに登録する
   */
  hel_bindjs_find_func: (objNameList: string[], funcName: string): number => {
    let targetObj = globalThis as Record<string, unknown>;

    for (const objName of objNameList) {
      const newObj = targetObj[objName];
      if (typeof newObj !== 'object' || newObj === null) {
        return -1;
      }
      targetObj = newObj as Record<string, unknown>;
    }

    const func = targetObj[funcName];
    if (func === undefined || typeof func !== 'function') {
      return -1;
    }

    const index = heliport_funcTable.length;
    heliport_funcTable.push(func);
    return index;
  },

  /**
   * IDで指定した登録済みの関数を呼び出す
   */
  hel_bindjs_call_func: (funcID: number, asyncReceiver: any, params: any[]): any => {
    const func = heliport_funcTable[funcID];
    if (!func) {
      return null;
    }

    const result = func(...params);

    if (asyncReceiver) {
      // asyncだった場合はここでPromiseの結果を受ける
      result.then(asyncReceiver).catch((error: any) => {
        // 失敗した場合は、コールバックで呼び出すHS関数の第1引数に ReturnFailure を返す。
        const returnFailure = {
          status: 'ng',
          errorCode: 'HEL03000000',
          data: {
            name: error.name ? error.name : '',
            message: error.message ? error.message : ''
          }
        };
        // ログ出力
        console.error(returnFailure);
        asyncReceiver(returnFailure);
      });
    } else {
      return result;
    }
  },

  /**
   * @remarks
   * C++から呼び出される
   */
  hel_debug_showstatus: (flag: boolean) => {
    const status = document.getElementById('status');
    if (status) {
      status.style.display = flag ? 'block' : 'none';
    }

    const log = document.getElementById('log');
    if (log) {
      log.style.display = flag ? 'block' : 'none';
    }
  },

  // ////////////////////////////
  // JSからHSのコンポーネントメソッド呼び出し
  // ////////////////////////////

  hel_CallSceneComponent,

  /**
   * Canvas側コンポーネント呼び出し
   */
  hel_CallCanvasComponent,

  /**
   * HeliScriptのイベントリスナーへイベント発火
   */
  hel_DispatchEvent,

  hel_SetServerTime,

  /**
   * 初回ローディング率をint値の0～100の値を返す
   * @returns
   * - ローディングが完了していたら100を返す
   * - 未完了であれば100未満を返す
   */
  hel_GetLoadingRate: (): number => {
    if (globalThis.isWasmInitialized) {
      return hel_setText(ETextParamType_GetFirstLoadingRate, '');
    }

    return 0;
  }
});
